// Settings menu — gear icon in the header that opens a popover with
// theme controls (light / dark / custom + accent color picker).
import React, { useEffect, useRef, useState } from "react";
import { Settings, Sun, Moon, Palette, Check } from "lucide-react";
import { useTheme } from "@/lib/ThemeProvider";

const ACCENT_PRESETS = [
  "#5F7161", // Sage (default)
  "#7E57C2", // Violet
  "#2E6E9E", // Ocean
  "#C66648", // Sunset
  "#D4A017", // Honey
  "#1F8A70", // Emerald
  "#E03E78", // Berry
  "#3F3F46", // Slate
];

const THEMES = [
  { id: "light",  label: "Light",  icon: Sun,     desc: "Soft cream background" },
  { id: "dark",   label: "Dark",   icon: Moon,    desc: "Easier on the eyes at night" },
  { id: "custom", label: "Custom", icon: Palette, desc: "Pick your own accent color" },
];

const SettingsMenu = () => {
  const { theme, setTheme, accent, setAccent } = useTheme();
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!popRef.current || !btnRef.current) return;
      if (!popRef.current.contains(e.target) && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        data-testid="settings-btn"
        aria-label="Settings"
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 rounded-full bg-white border border-[#D8D6D0] hover:bg-[#F4F6F1] text-[#5A5C58] flex items-center justify-center transition-colors"
      >
        <Settings size={15} />
      </button>

      {open && (
        <div
          ref={popRef}
          data-testid="settings-popover"
          role="dialog"
          aria-label="Settings"
          className="absolute right-0 mt-2 w-[300px] bg-white border border-[#D8D6D0] rounded-2xl shadow-[0_10px_40px_rgba(26,28,26,0.10)] p-4 z-[60]"
        >
          <div className="flex items-center gap-2 mb-3">
            <Settings size={13} className="text-[#5F7161]" />
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#5A5C58] font-medium">Settings</p>
          </div>

          <div className="mb-3">
            <p className="text-xs font-medium text-[#1A1C1A] mb-2">Theme</p>
            <div className="grid grid-cols-3 gap-1.5">
              {THEMES.map((t) => {
                const Icon = t.icon;
                const active = theme === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`theme-${t.id}-btn`}
                    onClick={() => setTheme(t.id)}
                    className={`flex flex-col items-center gap-1 px-2 py-3 rounded-xl border text-xs transition-all ${
                      active
                        ? "border-[#5F7161] bg-[#F4F6F1] text-[#1A1C1A]"
                        : "border-[#D8D6D0] hover:border-[#5F7161] hover:bg-[#F4F6F1] text-[#5A5C58]"
                    }`}
                  >
                    <Icon size={16} />
                    <span className="font-medium">{t.label}</span>
                    {active && <Check size={11} className="text-[#5F7161]" />}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#8A8C88] mt-2">
              {THEMES.find((t) => t.id === theme)?.desc}
            </p>
          </div>

          {theme === "custom" && (
            <div className="mt-3 pt-3 border-t border-[#D8D6D0]" data-testid="custom-accent-panel">
              <p className="text-xs font-medium text-[#1A1C1A] mb-2">Accent color</p>
              <div className="grid grid-cols-8 gap-1.5 mb-3">
                {ACCENT_PRESETS.map((c) => {
                  const active = accent.toLowerCase() === c.toLowerCase();
                  return (
                    <button
                      key={c}
                      data-testid={`accent-preset-${c.toLowerCase().replace("#", "")}`}
                      onClick={() => setAccent(c)}
                      title={c}
                      aria-label={`Accent ${c}`}
                      className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                        active ? "border-[#1A1C1A] ring-2 ring-offset-1 ring-[#1A1C1A]/20" : "border-white shadow-[0_0_0_1px_rgba(26,28,26,0.12)]"
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  );
                })}
              </div>
              <label className="flex items-center gap-2 text-xs text-[#5A5C58]">
                <span>Custom:</span>
                <input
                  data-testid="accent-color-input"
                  type="color"
                  value={accent}
                  onChange={(e) => setAccent(e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer bg-transparent border border-[#D8D6D0]"
                  aria-label="Custom accent color"
                />
                <span className="font-mono">{accent.toUpperCase()}</span>
              </label>
            </div>
          )}

          <p className="text-[10px] text-[#8A8C88] mt-3 pt-3 border-t border-[#D8D6D0]">
            Saved on this device · more settings coming soon
          </p>
        </div>
      )}
    </div>
  );
};

export default SettingsMenu;
